/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/
import { Button, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IAccountPreference,
    IAccountPreferences,
    IAccountProfileItem,
    IAccountProfileViewProps
} from '@msdyn365-commerce-modules/account-management/src/modules/account-profile/./account-profile';
import classnames from 'classnames';
import { IAccountProfileProps } from '../definition-extensions/account-profile.ext.props.autogenerated';
import { IGetMailchimpSubscriptionStatusData } from '../../../actions/get-mailchimp-subscription-status.action';
import { changeMailChimpSubscriptionStatusAsync, setUserBirthdayAsync } from '../../../actions/DataActionExtension.g';
import { MouseEventHandler } from 'react';
// from account-profile.ext.data.tsx file, which is now deleted
import { AsyncResult } from '@msdyn365-commerce/retail-proxy';
import { IGetBirthdayResponse } from '../../../actions/get-user-birthday.action';
import { padStart } from 'lodash';

interface IMailchimpSignupPreferences {
    heading: string;
    description: string;
    physicalHeading: string;
    physicalDescription: string;
    yesText: string;
    noText: string;
    state: IMailchimpState;
    handler: MouseEventHandler<HTMLButtonElement>;
    physicalHandler: MouseEventHandler<HTMLButtonElement>;
}

interface IBdayPreferences {
    state: IMailchimpState;
    editBirthday: MouseEventHandler<HTMLButtonElement>;
    submitBirthday: MouseEventHandler<HTMLButtonElement>;
    onEditBirthdayInput: any;
    label: string;
    submit: string;
    cancel: string;
    edit: string;
    notSet: string;
}

interface IMailchimpState {
    newsletterSubscribed: boolean;
    physicalSubscribed: boolean;
    dayString: string | undefined;
    bDay: number;
    bMonth: number;
    bYear: number;
    editingBirthday: boolean;
    bdayError: string;
}

// from account-profile.ext.data.tsx file, now deleted
export interface IAccountProfileData {
    mailchimpSubscriptionStatus: AsyncResult<IGetMailchimpSubscriptionStatusData>;
    birthday: AsyncResult<IGetBirthdayResponse>;
}

const AccountPreferencesSection: React.FC<IAccountPreferences> = ({ accountPreferences, heading, personalization, extraPreferences }) => {
    return (
        <Node {...accountPreferences}>
            {heading}
            <AccountPreferenceSection {...personalization} />
            {extraPreferences &&
                extraPreferences.map((preferenceSecton, index) => {
                    return <AccountPreferenceSection {...preferenceSecton} key={index} />;
                })}
        </Node>
    );
};

const AccountPreferenceSection: React.FC<IAccountPreference> = ({
    accountPreference,
    heading,
    description,
    buttonWrapper,
    buttonYesText,
    button,
    buttonNoText
}) => {
    return (
        <Node {...accountPreference}>
            {heading}
            {description}
            <Node {...buttonWrapper}>
                {buttonNoText}
                {button}
                {buttonYesText}
            </Node>
        </Node>
    );
};

const AccountProfileSection: React.FC<IAccountProfileItem> = ({ AccountProfileItem, heading, links, description }) => {
    return (
        <Node {...AccountProfileItem}>
            {heading}
            {description}
            {links}
        </Node>
    );
};

const BirthdaySection: React.FC<IBdayPreferences & IAccountProfileProps<IAccountProfileData>> = ({
    state,
    editBirthday,
    submitBirthday,
    onEditBirthdayInput,
    label,
    submit,
    cancel,
    edit,
    notSet
}) => {
    //TODO: Replace this with resource strings.
    if (state.editingBirthday) {
        return (
            <>
                <h2 className={'ms-account-profile__section-heading'}>{label} </h2>
                <input
                    type='date'
                    min={'1900-01-01'}
                    max={new Date().toDateString()}
                    id='bday'
                    name='bday-entry'
                    value={state.dayString}
                    onChange={onEditBirthdayInput}
                />
                <p className='ms-account-profile__error'>{state.bdayError}</p>
                <div>
                    <button className='ms-account-profile__edit-button' onClick={submitBirthday}>
                        {submit}
                    </button>
                    <br />
                    <button className='ms-account-profile__edit-button' onClick={editBirthday}>
                        {cancel}
                    </button>
                </div>
                <br />
            </>
        );
    } else if (state.bDay != -1 && state.bMonth != -1 && state.bYear != -1) {
        return (
            <>
                <h2 className={'ms-account-profile__section-heading'}>{label} </h2>
                <p id='bday'>{state.dayString}</p>
                <button className='ms-account-profile__edit-button' onClick={editBirthday}>
                    {edit}
                </button>
                <br />
                <br />
                <br />
            </>
        );
    } else {
        return (
            <>
                <h2 className={'ms-account-profile__section-heading'}>{label} </h2>
                <p id='bday'>{notSet}</p>
                <button className='ms-account-profile__edit-button' onClick={editBirthday}>
                    {edit}
                </button>
                <br />
                <br />
                <br />
            </>
        );
    }
};

const MailchimpSignupSection: React.FC<IMailchimpSignupPreferences & IAccountProfileProps<IAccountProfileData>> = ({
    noText,
    description,
    heading,
    physicalHeading,
    physicalDescription,
    yesText,
    state,
    handler,
    physicalHandler
}) => {
    const newsletterClassText = state.newsletterSubscribed ? 'enable' : 'disable';
    const physicalClassText = state.physicalSubscribed ? 'enable' : 'disable';
    const classNames = classnames('ms-account-profile__preference', `ms-account-profile__preference-web-tracking`, {
        'ms-account-profile__personalization-updating': true
    });
    const buttonWrapper = {
        className: classnames('ms-account-profile__toggle-wrapper', `ms-account-profile__toggle-${newsletterClassText}`)
    };

    const physButtonWrapper = {
        className: classnames('ms-account-profile__toggle-wrapper', `ms-account-profile__toggle-${physicalClassText}`)
    };
    const button = (
        <Button
            className={classnames('ms-account-profile__toggle-button', `ms-account-profile__toggle-${newsletterClassText}-button`)}
            aria-label={'Mailchimp Subscription'}
            aria-pressed={!state.newsletterSubscribed}
            onClick={handler}
        />
    );

    const physicalButton = (
        <Button
            className={classnames('ms-account-profile__toggle-button', `ms-account-profile__toggle-${physicalClassText}-button`)}
            aria-label={'Mailchimp Mail Subscription'}
            aria-pressed={!state.physicalSubscribed}
            onClick={physicalHandler}
        />
    );
    const buttonYesText = <span className={'ms-account-profile__toggle-enable-text'}>{yesText}</span>;
    const buttonNoText = <span className={'ms-account-profile__toggle-disable-text'}>{noText}</span>;

    const descriptionComponent = <p className={'ms-account-profile__section-description'}>{description}</p>;
    const physicalDescriptionComponent = <p className={'ms-account-profile__section-description'}>{physicalDescription}</p>;

    return (
        <>
            <h2>{heading}</h2>
            <Node className={classNames}>
                {descriptionComponent}
                <Node {...buttonWrapper}>
                    {buttonNoText}
                    {button}
                    {buttonYesText}
                </Node>
            </Node>
            <br />
            <h2>{physicalHeading}</h2>
            <Node className={classNames}>
                {physicalDescriptionComponent}
                <Node {...physButtonWrapper}>
                    {buttonNoText}
                    {physicalButton}
                    {buttonYesText}
                </Node>
            </Node>
        </>
    );
};

export class AccountProfileView extends React.PureComponent<
    IAccountProfileViewProps & IAccountProfileProps<IAccountProfileData>,
    IMailchimpState
> {
    constructor(props: IAccountProfileViewProps & IAccountProfileProps<IAccountProfileData>) {
        super(props);
        this.state = {
            newsletterSubscribed: false,
            physicalSubscribed: false,
            dayString: undefined,
            editingBirthday: false,
            bDay: -1,
            bMonth: -1,
            bYear: -1,
            bdayError: ''
        };
    }

    async componentDidMount(): Promise<void> {
        const {
            data: { mailchimpSubscriptionStatus, birthday }
        } = this.props;
        const status = await mailchimpSubscriptionStatus.result;
        const bday = await birthday.result;
        this.setState({
            newsletterSubscribed: status?.newsletterActive || false,
            physicalSubscribed: status?.physicalActive || false,
            bDay: bday?.Day || 0,
            bMonth: bday?.Month || 0,
            bYear: bday?.Year || 0,
            dayString: `${bday?.Year}-${padStart(bday?.Month.toString(), 2, '0')}-${padStart(bday?.Day.toString(), 2, '0')}`
        });
    }

    handleMailChimpSubscriptionStatusClick = () => {
        changeMailChimpSubscriptionStatusAsync(
            { callerContext: this.props.context.actionContext },
            !this.state.newsletterSubscribed,
            false
        ).then(res => {
            if (res.Success) {
                this.setState({ newsletterSubscribed: !this.state.newsletterSubscribed });
            }
        });
    };

    setBday = (year: number, month: number, day: number) => {
        this.setState({ dayString: `${year}-${month}-${day}`, bDay: day, bMonth: month, bYear: year });
    };

    editBirthday = () => {
        this.setState({
            editingBirthday: !this.state.editingBirthday,
            dayString: `${this.state.bYear}-${padStart(this.state.bMonth.toString(), 2, '0')}-${padStart(
                this.state.bDay.toString(),
                2,
                '0'
            )}`,
            bdayError: ''
        });
    };

    onEditBirthdayInput = (e: any) => {
        this.setState({ dayString: e.target.value });
        return false;
    };

    submitBirthday = async () => {
        if (this.state.dayString && this.props.data.customerInformation.result?.AccountNumber) {
            if (
                new Date(this.state.dayString) &&
                new Date(this.state.dayString).getFullYear() < new Date().getFullYear() &&
                new Date(this.state.dayString).getFullYear() > 1901 //Magic "Not before 1901 year plz"
            ) {
                //Is this a valid date, and is it before today?
                const splitDate: string[] = this.state.dayString.split('-');
                await setUserBirthdayAsync(
                    { callerContext: this.props.context.actionContext },
                    parseInt(splitDate[2]),
                    parseInt(splitDate[1]),
                    parseInt(splitDate[0]),
                    this.props.data.customerInformation.result.AccountNumber
                );

                this.setState({
                    editingBirthday: false,
                    bDay: parseInt(splitDate[2]),
                    bMonth: parseInt(splitDate[1]),
                    bYear: parseInt(splitDate[0]),
                    bdayError: ''
                });
            } else {
                this.setState({
                    bdayError: this.props.resources.invalidDateError,
                    dayString: `${this.state.bYear}-${padStart(this.state.bMonth.toString(), 2, '0')}-${padStart(
                        this.state.bDay.toString(),
                        2,
                        '0'
                    )}`
                });
            }
        }
    };

    handleMailChimpPhysicalSubscriptionStatusClick = () => {
        changeMailChimpSubscriptionStatusAsync(
            { callerContext: this.props.context.actionContext },
            !this.state.physicalSubscribed,
            true
        ).then(res => {
            if (res.Success) {
                this.setState({ physicalSubscribed: !this.state.physicalSubscribed });
            }
        });
    };

    public render(): JSX.Element | null {
        const {
            resources: {
                mailchimpHeading,
                mailchimpDescription,
                mailchimpYesText,
                mailchimpNoText,
                mailchimpPhysicalDescription,
                mailchimpPhysicalHeading,
                birthdayLabel,
                submitLabel,
                cancelLabel,
                editLabel,
                notSetLabel
            }
        } = this.props;
        return (
            <Module {...this.props.AccountProfile}>
                {this.props.accountProfileWrapper && (
                    <Node {...this.props.accountProfileWrapper}>
                        {this.props.heading}
                        <AccountProfileSection {...this.props.emailSection} />
                        <AccountProfileSection {...this.props.nameSection} />
                        {this.props.data.birthday.status === 'SUCCESS' ? (
                            <BirthdaySection
                                {...this.props}
                                state={this.state}
                                editBirthday={this.editBirthday}
                                submitBirthday={this.submitBirthday}
                                onEditBirthdayInput={this.onEditBirthdayInput}
                                label={birthdayLabel}
                                submit={submitLabel}
                                cancel={cancelLabel}
                                edit={editLabel}
                                notSet={notSetLabel}
                            />
                        ) : (
                            <></>
                        )}

                        <AccountPreferencesSection {...this.props.preferenceSection} />
                        <MailchimpSignupSection
                            {...this.props}
                            heading={mailchimpHeading}
                            description={mailchimpDescription}
                            physicalHeading={mailchimpPhysicalHeading}
                            physicalDescription={mailchimpPhysicalDescription}
                            yesText={mailchimpYesText}
                            noText={mailchimpNoText}
                            state={this.state}
                            handler={this.handleMailChimpSubscriptionStatusClick}
                            physicalHandler={this.handleMailChimpPhysicalSubscriptionStatusClick}
                        />
                    </Node>
                )}
                {this.props.customerAttributesWrapper && (
                    <Node {...this.props.customerAttributesWrapper}>{this.props.customerAttributesSection}</Node>
                )}
            </Module>
        );
    }
}

export default AccountProfileView;
